import React, {useEffect, useState} from "react";
import {useAuth} from "context/AuthContext";
import {useLocation, useNavigate} from "react-router-dom";
import LocationState from "types/LocationState";
import {useOperator} from "context/OperatorContext";
import {useMutation} from "react-query";
import Alert from "components/Alert";
import {TwoFactorAuthRecoveryForm} from "./Form/TwoFactorAuthRecoveryForm";
import {Transition} from "@headlessui/react";

// Types ----

type InitialValues = {
    recovery_code: string,
}

// Component ----

function TwoFactorAuthRecovery() {
    const navigate = useNavigate();
    const location = useLocation();
    const {refetchOperator} = useOperator();
    const auth = useAuth();
    const [loginComplete, setLoginComplete] = useState(false);

    const {
        mutateAsync: submitCode,
        data: result,
        isLoading,
        isError,
        reset: clearResult
    } = useMutation((code: string) => auth.twoFactorAuthRecovery(code), {
        onSuccess: async () => {
            await auth.refetchCurrentUser();
            await refetchOperator();
            setLoginComplete(true);
        },
    });

    const state = location.state as LocationState
    const from = state?.from?.pathname || '/';

    useEffect(() => {
        if (loginComplete && auth.user) {
            navigate(from, {replace: true});
        }
    }, [auth.user, from, loginComplete, navigate]);

    const handleChange = () => {
        if (!isLoading) clearResult();
    }

    async function handleSubmit({recovery_code}: InitialValues) {
        await submitCode(recovery_code.toString()).catch();
    }

    return (
      <Transition
        show={true}
        appear={true}
        enter="transition-all duration-1000"
        enterFrom="opacity-0 translate-y-5"
        enterTo="opacity-100 translate-x-0"
        className='w-full max-w-[22rem] md:min-w-[26.5rem] md:max-w-[26.5rem] flex flex-col gap-6'
      >
          <h1 className="text-2xl font-medium">Recovery code</h1>

          {isError &&
            <Alert
              type={Alert.types.ERROR}
              message='The provided two factor recovery code was invalid.'
            />
          }

          <TwoFactorAuthRecoveryForm onSubmit={handleSubmit} onChange={handleChange} />
      </Transition>
    )
}

export { TwoFactorAuthRecovery };